import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBackofficeUsers } from '@/hooks/backoffice/useBackofficeUsers'
import { DataTable, type Column } from '@/components/ui/data-table'
import { SearchInput } from '@/components/ui/search-input'
import { Pagination } from '@/components/ui/pagination'
import { StatusBadge } from '@/components/ui/status-badge'
import type { BackofficeUserListItem, UserRole } from '@/types/backoffice'

const ROLE_LABEL: Record<UserRole, string> = {
  ADMIN: 'Admin',
  OPERATOR: 'Operador',
  FINANCIAL: 'Financeiro',
}

const COLUMNS: Column<BackofficeUserListItem>[] = [
  { key: 'name', header: 'Nome' },
  { key: 'email', header: 'Email', render: (r) => r.email },
  {
    key: 'tenant',
    header: 'Transportadora',
    render: (r) => r.tenant.name,
  },
  {
    key: 'role',
    header: 'Role',
    render: (r) => ROLE_LABEL[r.role],
  },
  { key: 'active', header: 'Status', render: (r) => <StatusBadge active={r.active} /> },
]

type ActiveFilter = 'all' | 'active' | 'inactive'
const FILTER_LABELS: Record<ActiveFilter, string> = { all: 'Todos', active: 'Ativos', inactive: 'Inativos' }

export function BackofficeUsersListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')
  const [roleFilter, setRoleFilter] = useState<UserRole | 'ALL'>('ALL')
  const [page, setPage] = useState(1)

  const activeParam =
    activeFilter === 'active' ? true : activeFilter === 'inactive' ? false : undefined

  const { data, isLoading } = useBackofficeUsers({
    search,
    active: activeParam,
    role: roleFilter === 'ALL' ? undefined : roleFilter,
    page,
  })

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Usuários</h1>
        <p className="text-sm text-muted-foreground">Todos os usuários da plataforma.</p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder="Buscar por nome ou email…"
          className="flex-1"
        />
        <select
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value as UserRole | 'ALL')
            setPage(1)
          }}
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        >
          <option value="ALL">Todas as roles</option>
          <option value="ADMIN">Admin</option>
          <option value="OPERATOR">Operador</option>
          <option value="FINANCIAL">Financeiro</option>
        </select>
        <div className="flex overflow-hidden rounded-md border border-input text-sm">
          {(Object.keys(FILTER_LABELS) as ActiveFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => {
                setActiveFilter(f)
                setPage(1)
              }}
              className={`px-3 py-1.5 transition-colors ${
                activeFilter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted/50'
              }`}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        columns={COLUMNS}
        data={data?.data ?? []}
        keyExtractor={(r) => r.id}
        onRowClick={(r) => navigate(`/backoffice/users/${r.id}`)}
        isLoading={isLoading}
        emptyTitle="Nenhum usuário encontrado"
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          total={data.total}
          perPage={data.perPage}
          onChange={setPage}
        />
      )}
    </div>
  )
}
